import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import SeoCopy from "@/components/SeoCopy";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const expired = params.get("expired") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      if (user.role === "kid_worker") navigate("/worker");
      else if (user.role === "content_editor") navigate("/editor");
      else if (["admin", "superadmin", "parent"].includes(user.role)) navigate("/admin");
      else navigate(next);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
      <div className="card-clay p-7">
        <h1 className="font-fredoka text-3xl font-bold text-slate-900" data-testid="login-title">Welcome back</h1>
        <p className="text-slate-600 text-sm mt-1">Log in to schedule and track your pickups.</p>
        {expired && (
          <div className="mt-3 p-3 rounded-xl bg-amber-50 border-2 border-amber-200 text-amber-900 text-sm" data-testid="login-expired-banner">
            Your session expired. Please log in again to continue.
          </div>
        )}
        <form onSubmit={onSubmit} className="mt-5 space-y-3">
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Email</div>
            <input type="email" required className="input-clay h-11 px-3 w-full" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
          </label>
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Password</div>
            <input type="password" required className="input-clay h-11 px-3 w-full" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
          </label>
          <button type="submit" disabled={loading} className="btn-push-green w-full py-3" data-testid="login-submit">
            {loading ? "Logging in…" : "Log in"}
          </button>
        </form>
        <div className="text-sm text-slate-600 mt-4 text-center">
          New here? <Link to="/register" className="text-green-700 font-bold underline" data-testid="login-to-register">Create an account</Link>
        </div>
        <SeoCopy
          title="Why log in to Bag Dash Kids?"
          links={[
            { to: "/about", label: "Meet the Kids" },
            { to: "/schedule", label: "Schedule a pickup" },
            { to: "/quick-pay", label: "Pay on the spot" },
            { to: "/policies", label: "Pricing & policies" },
          ]}
        >
          <p>Customers log in to schedule weekly trash pickups, view upcoming and past stops, manage their subscription, track <strong>Dash Rewards</strong> loyalty points, and share their referral code with neighbors. Every paid pickup earns points toward free pickups and bin cleanings.</p>
          <p>Don&apos;t have an account yet? Create one in seconds — you only need an email and phone number to get started.</p>
        </SeoCopy>
      </div>
    </div>
  );
}
